import { useState, useEffect, useCallback } from 'react';

const TOKEN_KEY = 'auth_token';
const AUTH_URL = 'https://auth.nicefox.net';

// Module-level flag pour éviter le double traitement (même avec React StrictMode)
let globalTokenProcessed = false;

export interface User {
  id: string;
  email: string;
  role: 'user' | 'admin';
}

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkToken = useCallback(async (token: string) => {
    try {
      const res = await fetch('/api/me', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const userData = await res.json();
        setUser(userData);
        setError(null);
        return true;
      } else {
        const errData = await res.json().catch(() => ({ error: 'Unknown' }));
        console.error('Token verification failed:', errData);
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
        setError(errData.error || 'Session expirée');
        return false;
      }
    } catch (err) {
      console.error('Auth check error:', err);
      setUser(null);
      setError('Erreur de connexion');
      return false;
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      let token: string | null = null;

      // 1. Vérifie si on doit traiter le token de l'URL
      if (!globalTokenProcessed) {
        const params = new URLSearchParams(window.location.search);
        const tokenFromUrl = params.get('token');
        
        if (tokenFromUrl) {
          console.log('🔑 Token trouvé dans l\'URL');
          token = tokenFromUrl;
          globalTokenProcessed = true;
          
          try {
            localStorage.setItem(TOKEN_KEY, token);
            console.log('✅ Token sauvegardé');
            window.history.replaceState({}, '', window.location.pathname);
          } catch (e) {
            console.error('❌ Erreur sauvegarde:', e);
          }
        }
      }

      // 2. Récupère depuis localStorage si pas trouvé dans URL
      if (!token) {
        token = localStorage.getItem(TOKEN_KEY);
      }

      if (!token) {
        console.log('⚠️ Aucun token');
        setLoading(false);
        return;
      }

      // 3. Vérifie le token
      console.log('🔍 Vérification token...');
      await checkToken(token);
      setLoading(false);
    };

    initAuth();
  }, [checkToken]);

  const login = useCallback(() => {
    globalTokenProcessed = false; // Reset pour la prochaine connexion
    const returnUrl = encodeURIComponent(window.location.href);
    window.location.href = `${AUTH_URL}/login?redirect=${returnUrl}`;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    globalTokenProcessed = false;
    setUser(null);
    setError(null);
    window.location.href = '/';
  }, []);

  const getToken = useCallback(() => {
    return localStorage.getItem(TOKEN_KEY);
  }, []);

  return {
    user,
    loading,
    error,
    login,
    logout,
    getToken,
    isAuthenticated: !!user,
  };
};
